import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Popover, Select, Input, message, Tooltip, Dropdown } from 'antd';
import { colors } from '../../design-system/tokens/colors';
import type { TableColumn, CellType, DataType } from './types';
import { XIcon, SearchIcon, MailIcon, ChatIcon, PlusIcon, LockClosedIcon, QuestionMarkCircleIcon } from '@heroicons/react/outline';
import { PlatformIcon, OutlookIcon, ShopifyIcon, GmailIcon, BitlyIcon } from '../../design-system/components/data-display/EmailProviderIcons';
// import { OutreachConfig } from './columnConfigs/OutreachConfig';
import { shopifyStatusService } from '../../services/shopifyStatusService';
import { TagOutlined, FileTextOutlined } from '@ant-design/icons';
import { generatePrompt } from '../../services/promptGeneratorService';

const { TextArea } = Input;

interface ColumnConfigPopoverProps {
  column: TableColumn | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (updatedColumn: TableColumn) => void;
  onCancel: () => void;
  availableColumns?: TableColumn[];
  rows?: any[];
}

const PopoverContent = styled.div`
  width: 480px;
  max-height: 600px;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  
  &::-webkit-scrollbar {
    width: 3px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 2px;
  }
`;

const FormField = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FieldLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HelpIcon = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: help;
  color: ${colors.text.tertiary};
  opacity: 0.6;
  transition: opacity 0.15s ease;
  
  &:hover {
    opacity: 1;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const HelpText = styled.div`
  font-size: 11px;
  color: ${colors.text.tertiary};
  margin-bottom: 12px;
  opacity: 0.8;
  line-height: 1.4;
`;

const PopoverFooter = styled.div`
  padding: 12px 16px;
  border-top: 1px solid ${colors.border.secondary};
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'text' }>`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${({ $variant = 'secondary' }) => {
    if ($variant === 'primary') {
      return `
        background: ${colors.brand.primary};
        color: white;
        &:hover:not(:disabled) {
          background: ${colors.brand.primaryHover};
        }
      `;
    } else if ($variant === 'text') {
      return `
        background: transparent;
        color: ${colors.text.secondary};
        padding: 6px 8px;
        &:hover:not(:disabled) {
          color: ${colors.text.primary};
          background: rgba(0, 0, 0, 0.03);
        }
      `;
    } else {
      return `
        background: white;
        color: ${colors.text.primary};
        border: 1px solid ${colors.border.secondary};
        &:hover:not(:disabled) {
          background: ${colors.background.secondary};
        }
      `;
    }
  }}
`;

const StyledInput = styled(Input)`
  font-size: 12px !important;
  padding: 6px 10px !important;
  border-radius: 6px !important;
  border: none !important;
  background: #f8f9fa !important;
  box-shadow: none !important;
  
  &::placeholder {
    color: ${colors.text.tertiary};
    opacity: 0.8;
  }
`;

const StyledTextArea = styled(TextArea)`
  font-size: 12px !important;
  padding: 8px 10px !important;
  border-radius: 6px !important;
  border: none !important;
  background: #f8f9fa !important;
  box-shadow: none !important;
  resize: none !important;
  
  &::placeholder {
    color: ${colors.text.tertiary};
    opacity: 0.8;
  }
`;

const PromptInputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PromptFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const AIGenerateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: transparent;
  border: none;
  border-radius: 4px;
  font-size: 10px;
  color: ${colors.text.tertiary};
  cursor: pointer;
  transition: all 0.15s ease;
  text-decoration: none;

  &:hover:not(:disabled) {
    background: ${colors.background.secondary};
    color: ${colors.text.primary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 12px;
    height: 12px;
    opacity: 0.7;
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;
  
  .ant-select-selector {
    font-size: 12px !important;
    border-radius: 6px !important;
    border: none !important;
    background: #f8f9fa !important;
    box-shadow: none !important;
  }
`;

// Platform selector components
const UnifiedSourceContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const UnifiedSourceOption = styled.button<{ $selected: boolean; $locked?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${props => {
    if (props.$locked) return '#e6e6e6';
    return props.$selected ? '#3b82f6' : 'rgba(0, 0, 0, 0.08)';
  }};
  border-radius: 6px;
  background: ${props => {
    if (props.$locked) return '#fafafa';
    return props.$selected ? 'rgba(59, 130, 246, 0.05)' : 'transparent';
  }};
  cursor: ${props => props.$locked ? 'not-allowed' : 'pointer'};
  transition: all 0.15s ease;
  opacity: ${props => props.$locked ? 0.5 : 1};
  position: relative;
  
  &:hover:not(:disabled) {
    border-color: ${props => {
      if (props.$locked) return '#e6e6e6';
      return props.$selected ? '#2563eb' : 'rgba(0, 0, 0, 0.15)';
    }};
    background: ${props => {
      if (props.$locked) return '#fafafa';
      return props.$selected ? 'rgba(59, 130, 246, 0.08)' : 'rgba(0, 0, 0, 0.02)';
    }};
  }
`;

const SourceLockIcon = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid #e6e6e6;
  border-radius: 3px;
  
  svg {
    width: 8px;
    height: 8px;
    color: ${colors.text.secondary};
    opacity: 0.6;
  }
`;

// Prompt examples tip
const PromptTip = styled.div`
  padding: 10px 12px;
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.15);
  border-radius: 6px;
  font-size: 11px;
  color: ${colors.text.secondary};
  line-height: 1.5;
  margin-top: 8px;
`;

const PromptTipHeader = styled.div`
  font-weight: 500;
  margin-bottom: 6px;
  color: ${colors.text.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PromptTipClose = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${colors.text.tertiary};
  opacity: 0.6;
  transition: opacity 0.15s ease;
  
  &:hover {
    opacity: 1;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const PromptTipItem = styled.div`
  margin-bottom: 4px;
  cursor: pointer;
  transition: color 0.15s ease;
  
  &:hover {
    color: ${colors.text.primary};
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

// Shopify-specific components
const ShopifySubtypeContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ShopifySubtypeOption = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid ${props => props.$selected ? 'rgba(59, 130, 246, 0.3)' : 'rgba(0, 0, 0, 0.06)'};
  border-radius: 4px;
  background: ${props => props.$selected ? 'rgba(59, 130, 246, 0.04)' : 'transparent'};
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 11px;
  color: ${props => props.$selected ? '#3b82f6' : colors.text.tertiary};
  font-weight: 500;
  
  &:hover {
    border-color: ${props => props.$selected ? 'rgba(59, 130, 246, 0.4)' : 'rgba(0, 0, 0, 0.1)'};
    background: ${props => props.$selected ? 'rgba(59, 130, 246, 0.06)' : 'rgba(0, 0, 0, 0.02)'};
    color: ${props => props.$selected ? '#3b82f6' : colors.text.secondary};
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const UnifiedSourceSelector: React.FC<{
  selectedSource: string | null;
  onSourceSelect: (source: string) => void;
  mode?: 'tracking' | 'action';
}> = ({ selectedSource, onSourceSelect, mode = 'tracking' }) => {
  const trackingSources = [
    { id: 'instagram', name: 'Instagram', icon: <PlatformIcon platform="instagram" size={14} />, locked: false },
    { id: 'tiktok', name: 'TikTok', icon: <PlatformIcon platform="tiktok" size={14} />, locked: false },
    { id: 'outlook', name: 'Outlook', icon: <OutlookIcon size={14} />, locked: false },
    { id: 'gmail', name: 'Gmail', icon: <GmailIcon size={14} />, locked: false }
  ];

  const actionSources = [
    { id: 'shopify', name: 'Shopify', icon: <ShopifyIcon size={14} />, locked: false },
    { id: 'outlook', name: 'Outlook', icon: <OutlookIcon size={14} />, locked: true },
    { id: 'gmail', name: 'Gmail', icon: <GmailIcon size={14} />, locked: true },
    { id: 'bitly', name: 'Bitly', icon: <BitlyIcon size={14} />, locked: true }
  ];

  const sources = mode === 'tracking' ? trackingSources : actionSources;

  return (
    <UnifiedSourceContainer>
      {sources.map((source) => {
        return (
          <UnifiedSourceOption
            key={source.id}
            $selected={selectedSource === source.id}
            $locked={source.locked}
            onClick={() => {
              if (!source.locked) {
                onSourceSelect(source.id);
              }
            }}
            disabled={source.locked}
            type="button"
            title={source.locked ? `${source.name} (Coming Soon)` : source.name}
          >
            {source.icon}
            {source.locked && (
              <SourceLockIcon>
                <LockClosedIcon />
              </SourceLockIcon>
            )}
          </UnifiedSourceOption>
        );
      })}
    </UnifiedSourceContainer>
  );
};

export const ColumnConfigPopover: React.FC<ColumnConfigPopoverProps> = ({
  column,
  open,
  onOpenChange,
  onSave,
  onCancel,
  availableColumns = [],
  rows = []
}) => {
  const [cellType, setCellType] = useState<CellType>(column?.cell_type || 'normal');
  const [columnName, setColumnName] = useState(column?.name || '');
  const [prompt, setPrompt] = useState(column?.prompt || '');
  const [trackSource, setTrackSource] = useState<string | null>(null);
  const [promptTipDismissed, setPromptTipDismissed] = useState(false);
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  
  // Track Posts specific
  const [searchCriteria, setSearchCriteria] = useState<string>('');
  const [searchFrequency, setSearchFrequency] = useState<'daily' | 'weekly' | 'manual'>('manual');
  
  // Shopify specific
  const [shopifySubtype, setShopifySubtype] = useState<'discount_code' | 'analytics'>('discount_code');
  const [shopifyPriceRuleId, setShopifyPriceRuleId] = useState<string>('');
  const [shopifyPriceRuleName, setShopifyPriceRuleName] = useState<string>('');
  const [codeType, setCodeType] = useState<'same' | 'individual'>('individual');
  const [sameCodeValue, setSameCodeValue] = useState('');
  const [handleLength, setHandleLength] = useState(4);
  const [codeSuffix, setCodeSuffix] = useState('');
  const [shopifyPriceRules, setShopifyPriceRules] = useState<Array<{id: string; title: string}>>([]);
  const [loadingPriceRules, setLoadingPriceRules] = useState(false);
  
  // Outreach specific
  const [outreachContent, setOutreachContent] = useState<string>('');
  const [outreachSubject, setOutreachSubject] = useState<string>('');

  // Initialize form data when column changes or popover opens
  useEffect(() => {
    if (!open) return; // Only initialize when popover is actually open
    
    if (column) {
      // Editing existing column - go to step 2
      setCellType(column.cell_type === 'shopify' && column.shopify_config?.subtype === 'discount_code' 
        ? 'shopify_action' 
        : column.cell_type);
      setColumnName(column.name);
      setPrompt(column.prompt || '');
      
      // Set track source based on cell type
      if (column.cell_type === 'normal') {
        const firstPlatform = column.platform_resources?.[0]?.platform || null;
        setTrackSource(firstPlatform);
      } else if (column.cell_type === 'communication') {
        setTrackSource('outlook');
      } else if (column.cell_type === 'track_posts') {
        setTrackSource(column.track_posts_config?.platform || null);
        setSearchCriteria(column.track_posts_config?.search_criteria || '');
        setSearchFrequency(column.track_posts_config?.search_frequency || 'manual');
      }
      
      // Shopify initialization
      if (column.cell_type === 'shopify' && column.shopify_config) {
        setShopifySubtype(column.shopify_config.subtype || 'discount_code');
        setShopifyPriceRuleId(column.shopify_config.price_rule_id || '');
        setShopifyPriceRuleName(column.shopify_config.price_rule_name || '');
        
        const template = column.shopify_config.code_name_template || '';
        const handleMatch = template.match(/\{handle:first:(\d+):upper\}(.*)$/);
        
        if (handleMatch) {
          setCodeType('individual');
          setHandleLength(parseInt(handleMatch[1]));
          setCodeSuffix(handleMatch[2] || '');
          setSameCodeValue('');
        } else {
          setCodeType('same');
          setSameCodeValue(template);
          setHandleLength(4);
          setCodeSuffix('');
        }
      }
      
      // Outreach initialization
      if (column.outreach_config) {
        setOutreachContent(column.outreach_config.template || '');
        setOutreachSubject(column.outreach_config.subject || '');
      }
      
    } else {
      // New column - start with normal type
      setCellType('normal');
      setColumnName('');
      setPrompt('');
      setTrackSource(null);
      setSearchCriteria('');
      setSearchFrequency('manual');
      setOutreachContent('');
      setOutreachSubject('');
      setShopifyPriceRuleId('');
      setShopifyPriceRuleName('');
      setCodeType('individual');
      setSameCodeValue('');
      setHandleLength(4);
      setCodeSuffix('');
    }
    setPromptTipDismissed(false);
  }, [column, open]);

  // Fetch Shopify data when needed
  useEffect(() => {
    const fetchShopifyData = async () => {
      if (!open || cellType !== 'shopify_action') return;
      
      try {
        const status = await shopifyStatusService.getStatus();
        if (status?.isConnected && status?.channel_id) {
          setLoadingPriceRules(true);
          try {
            const { shopifyActionsService } = await import('../../services/shopifyActionsService');
            const rules = await shopifyActionsService.listPriceRules(status.channel_id, { limit: 250 });
            setShopifyPriceRules(rules.map((r: any) => ({ id: String(r.id), title: r.title })));
          } catch (err) {
            console.error('Failed to load price rules:', err);
          } finally {
            setLoadingPriceRules(false);
          }
        }
      } catch (error) {
        console.error('Failed to fetch Shopify status:', error);
      }
    };

    fetchShopifyData();
  }, [open, cellType]);

  const handleSave = () => {
    // Validation
    if (!columnName.trim()) {
      message.error('Please enter a column name');
      return;
    }

    // Build column data based on cell type
    let actualCellType = cellType;
    
    if (cellType === 'shopify_action') {
      actualCellType = 'shopify';
    }
    
    const columnData: TableColumn = {
      id: column?.id || `col_${Date.now()}`,
      name: columnName,
      cell_type: actualCellType,
      data_type: 'text',
      response_format: 'short',
      label_options: [],
    };

    // Add cell-type-specific config
    if (actualCellType === 'normal') {
      if (!trackSource || !['tiktok', 'instagram', 'outlook', 'gmail'].includes(trackSource)) {
        message.error('Please select a platform (TikTok, Instagram, Outlook, or Gmail)');
        return;
      }
      if (!prompt.trim()) {
        message.error('Please describe what to extract or analyze');
        return;
      }
      columnData.prompt = prompt;
      
      // Handle email sources differently from social media sources
      if (trackSource === 'outlook' || trackSource === 'gmail') {
        columnData.email_provider = trackSource;
        columnData.communication_sources = trackSource === 'outlook' 
          ? ['email_history', 'outlook_conversations']
          : ['email_history', 'gmail_conversations'];
        columnData.requires_thread = true;
      } else {
        // Social media sources
        columnData.platform_resources = [{ platform: trackSource, type: 'social_media' }];
      }
    } else if (actualCellType === 'communication') {
      if (!prompt.trim()) {
        message.error('Please describe what to extract or analyze');
        return;
      }
      columnData.prompt = prompt;
      columnData.email_provider = 'outlook';
      columnData.communication_sources = ['email_history', 'outlook_conversations'];
      columnData.requires_thread = true;
    } else if (actualCellType === 'track_posts') {
      if (!trackSource) {
        message.error('Please select a platform');
        return;
      }
      if (!searchCriteria.trim()) {
        message.error('Please describe what to look for in posts');
        return;
      }
      columnData.track_posts_config = {
        platform: trackSource as 'tiktok' | 'instagram',
        search_criteria: searchCriteria.trim(),
        auto_search: false,
        search_frequency: searchFrequency
      };
    } else if (actualCellType === 'shopify') {
      let finalTemplate = '';
      if (codeType === 'same') {
        finalTemplate = sameCodeValue.toUpperCase();
      } else {
        finalTemplate = `{handle:first:${handleLength}:upper}${codeSuffix}`;
      }
      
      columnData.shopify_config = {
        subtype: 'discount_code',
        code_name_template: finalTemplate,
        allow_custom_name: false,
        price_rule_id: shopifyPriceRuleId,
        price_rule_name: shopifyPriceRuleName
      };
    } else if (actualCellType === 'outreach') {
      columnData.outreach_config = {
        template: outreachContent,
        subject: outreachSubject,
        send_from_email: '',
        send_from_name: ''
      };
    }

    onSave(columnData);
    onOpenChange(false);
  };

  const getPromptExamples = () => {
    switch (trackSource) {
      case 'tiktok':
        return [
          { display: 'Follower count', prompt: 'Get this creator\'s follower count specifically from their TikTok account only.' },
          { display: 'Engagement rate', prompt: 'Calculate this creator\'s engagement rate specifically for TikTok by analyzing their latest 5 TikTok videos.' },
          { display: 'Content type', prompt: 'Analyze what type of content this creator posts specifically on TikTok.' },
        ];
      case 'instagram':
        return [
          { display: 'Follower count', prompt: 'Get this creator\'s follower count specifically from their Instagram account only.' },
          { display: 'Engagement rate', prompt: 'Calculate this creator\'s engagement rate specifically for Instagram by analyzing their latest 5 Instagram posts.' },
          { display: 'Content aesthetic', prompt: 'Analyze the aesthetic and visual style of this creator\'s Instagram feed.' },
        ];
      case 'outlook':
      case 'gmail':
        return [
          { display: 'Response status', prompt: "What is the creator's response status? Answer with: No Response, Declined, or Interested" },
          { display: 'Shipping address', prompt: 'What shipping address did the creator provide?' },
        ];
      default:
        return [];
    }
  };

  const promptExamples = getPromptExamples();

  const renderConfigContent = () => {
    switch (cellType) {
      case 'normal':
      case 'communication':
        return (
          <>
            <FormField>
              <FieldLabel>Select Data Source</FieldLabel>
              <HelpText>
                {cellType === 'normal' ? 'Choose which platform to research' : 'Choose what to track and monitor'}
              </HelpText>
              <UnifiedSourceSelector
                selectedSource={trackSource}
                onSourceSelect={(source) => {
                  setTrackSource(source);
                  setPromptTipDismissed(false);
                }}
                mode="tracking"
              />
            </FormField>

            <FormField>
              <FieldLabel>What should the AI extract or analyze?</FieldLabel>
              <HelpText>
                Describe what information the AI should find or calculate
              </HelpText>
              <PromptInputContainer>
                <StyledTextArea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={trackSource === 'outlook' 
                    ? "e.g., What is the creator's response status?"
                    : "e.g., Get follower count"}
                  rows={4}
                />
                <PromptFooter>
                  <AIGenerateButton
                    onClick={async () => {
                      if (!trackSource || !columnName.trim() || isGeneratingPrompt) return;
                      
                      setIsGeneratingPrompt(true);
                      try {
                        const generatedPrompt = await generatePrompt({
                          column_name: columnName.trim(),
                          sources: [trackSource]
                        });
                        
                        setPrompt(generatedPrompt);
                      } catch (error) {
                        console.error('Error generating prompt:', error);
                        message.error('Failed to generate prompt. Please try again.');
                      } finally {
                        setIsGeneratingPrompt(false);
                      }
                    }}
                    disabled={!trackSource || !columnName.trim() || isGeneratingPrompt}
                  >
                    {isGeneratingPrompt ? 'Generating...' : 'AI Generate'}
                  </AIGenerateButton>
                </PromptFooter>
              </PromptInputContainer>
              
              {promptExamples.length > 0 && !promptTipDismissed && (
                <PromptTip>
                  <PromptTipHeader>
                    <span>Example prompts (click to use):</span>
                    <PromptTipClose onClick={() => setPromptTipDismissed(true)} type="button">
                      <XIcon />
                    </PromptTipClose>
                  </PromptTipHeader>
                  {promptExamples.map((example, index) => (
                    <PromptTipItem 
                      key={index}
                      onClick={() => setPrompt(example.prompt)}
                    >
                      • {example.display}
                    </PromptTipItem>
                  ))}
                </PromptTip>
              )}
            </FormField>
          </>
        );

      case 'track_posts':
        return (
          <>
            <FormField>
              <FieldLabel>Select Platform</FieldLabel>
              <HelpText>
                Choose which platform to search for posts
              </HelpText>
              <UnifiedSourceSelector
                selectedSource={trackSource}
                onSourceSelect={setTrackSource}
                mode="tracking"
              />
            </FormField>

            <FormField>
              <FieldLabel>What to look for in posts?</FieldLabel>
              <HelpText>
                Describe what the AI should search for (hashtags, mentions, keywords, etc.)
              </HelpText>
              <StyledTextArea
                value={searchCriteria}
                onChange={(e) => setSearchCriteria(e.target.value)}
                placeholder="e.g., Posts with #brandname, Posts mentioning @yourbrand"
                rows={4}
              />
            </FormField>
          </>
        );

      case 'shopify_action':
        const buildPreviewCode = () => {
          if (codeType === 'same') {
            return sameCodeValue || 'Enter code above';
          } else {
            const handle = 'matfashionista';
            const code = handle.substring(0, handleLength).toUpperCase() + codeSuffix;
            return code || 'Configure above';
          }
        };

        return (
          <>
            <FormField>
              <FieldLabel>Select Price Rule</FieldLabel>
              <StyledSelect
                value={shopifyPriceRuleId}
                onChange={(value) => {
                  setShopifyPriceRuleId(value as string);
                  const rule = shopifyPriceRules.find(r => r.id === value);
                  setShopifyPriceRuleName(rule?.title || '');
                }}
                placeholder="Select a price rule"
                loading={loadingPriceRules}
                disabled={loadingPriceRules}
              >
                {shopifyPriceRules.map(rule => (
                  <Select.Option key={rule.id} value={rule.id}>
                    {rule.title}
                  </Select.Option>
                ))}
              </StyledSelect>
            </FormField>

            <FormField>
              <FieldLabel>Discount Code Format</FieldLabel>
              <HelpText style={{ marginBottom: '8px' }}>Choose how discount codes are generated</HelpText>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  fontSize: '12px',
                  marginBottom: '6px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="radio"
                    checked={codeType === 'same'}
                    onChange={() => setCodeType('same')}
                  />
                  Same code for all creators
                </label>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="radio"
                    checked={codeType === 'individual'}
                    onChange={() => setCodeType('individual')}
                  />
                  Individual codes based on creator name
                </label>
              </div>

              {codeType === 'same' ? (
                <StyledInput
                  value={sameCodeValue}
                  onChange={(e) => setSameCodeValue(e.target.value.toUpperCase())}
                  placeholder="e.g., SPRING2024"
                />
              ) : (
                <>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '10px', color: colors.text.secondary, marginBottom: '4px' }}>
                      Characters from handle
                    </div>
                    <StyledInput
                      type="number"
                      min={1}
                      max={20}
                      value={handleLength}
                      onChange={(e) => setHandleLength(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                      style={{ width: '80px' }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', color: colors.text.secondary, marginBottom: '4px' }}>
                      Suffix (optional)
                    </div>
                    <StyledInput
                      value={codeSuffix}
                      onChange={(e) => setCodeSuffix(e.target.value.toUpperCase())}
                      placeholder="e.g., 20"
                      style={{ width: '80px' }}
                    />
                  </div>
                </>
              )}

              <div style={{
                padding: '8px 10px',
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.06)',
                borderRadius: '4px',
                fontSize: '10px',
                color: colors.text.tertiary,
                fontFamily: 'monospace',
                marginTop: '8px'
              }}>
                <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                  {codeType === 'same' ? 'Preview:' : 'Preview for @matfashionista:'}
                </div>
                {buildPreviewCode()}
              </div>
            </FormField>
          </>
        );

      // case 'outreach':
      //   return (
      //     <FormField>
      //       <OutreachConfig
      //         outreachContent={outreachContent}
      //         onContentChange={setOutreachContent}
      //         outreachSubject={outreachSubject}
      //         onSubjectChange={setOutreachSubject}
      //         availableColumns={availableColumns}
      //         rows={rows}
      //       />
      //     </FormField>
      //   );

      default:
        return null;
    }
  };

  const renderContent = () => (
    <>
      <ScrollableContent>
        {/* Label field - always at the top */}
        <FormField>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <FieldLabel>
              Label
              <Tooltip title="How this column will appear in the table">
                <HelpIcon>
                  <QuestionMarkCircleIcon />
                </HelpIcon>
              </Tooltip>
            </FieldLabel>
          </div>
          <StyledInput
            value={columnName}
            onChange={(e) => setColumnName(e.target.value)}
            placeholder="e.g., Follower Count"
          />
        </FormField>
        
        {renderConfigContent()}
      </ScrollableContent>
    </>
  );

  const content = (
    <PopoverContent onClick={(e) => e.stopPropagation()}>
      {renderContent()}

      <PopoverFooter>
        <Button $variant="text" onClick={() => {
          onOpenChange(false);
          onCancel();
        }}>
          Cancel
        </Button>
        <Button $variant="primary" onClick={handleSave}>
          Save Changes
        </Button>
      </PopoverFooter>
    </PopoverContent>
  );

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      placement="bottom"
      trigger="click"
      overlayInnerStyle={{ padding: 0 }}
      content={content}
    >
      <div style={{ width: 1, height: 1 }} />
    </Popover>
  );
};
